<template>
	<div class="voucher-part" :class="{'voucher-empty': !totalDiscount, 'voucher-from-modal': isKioskChannel}" v-if="featureToggles.voucherEnabled">
		<template v-if="isBM100 && isMobileChannel">
			<v-expansion-panel>
				<v-expansion-panel-content>
					<template v-slot:header>
						<v-flex>
							<p class="voucher-header">{{$t("utilityComponents.voucherInput.header")}}</p>
						</v-flex>
					</template>
					<v-flex class="voucher-body">
						<v-text-field
							class="voucher-style"
							v-model="voucher"
							:aria-label="$t('utilityComponents.voucherInput.header')"
						/>
						<v-btn class="apply-btn" @click="applyVoucher" :disabled="!voucher">
							{{$t("common.ok")}}
						</v-btn>
					</v-flex>
				</v-expansion-panel-content>
			</v-expansion-panel>
		</template>
		<v-layout v-else column wrap class="voucher-input-container-spacing" :class="{'voucher-modal': isKioskChannel}">
			<v-flex>
				<p class="voucher-header">{{$t("utilityComponents.voucherInput.header")}}</p>
			</v-flex>
			<v-flex class="voucher-body">
				<v-text-field
					class="voucher-style"
					data-cy="voucher-input"
					v-model="voucher"
					:aria-label="$t('utilityComponents.voucherInput.header')"
					@focus="$emit('show-keyboard')"
				/>
				<v-btn class="apply-btn" @click="applyVoucher" :disabled="!voucher" data-cy="apply-voucher">
					{{$t("common.ok")}}
				</v-btn>
			</v-flex>
		</v-layout>
	</div>
</template>

<script>
import {mapGetters} from "vuex";
import {formatPriceZero} from "../../domains2/libraries/Utils";
import OrderHelperObject from "../../domains2/orders/OrderHelperObject";
import {DomainCoreGeneration} from "../../domains2/libraries/DomainCoreGeneration";
import appConfig, {channelEnum} from "../../appconfig";
import {voucherValidationHelper} from "../../helper/VoucherValidationHelper";
import {evaluateCustomerType, getExceededPriceRules} from "../../libraries/utils";
import { convertProductSelectionsToMap } from "../../domains2/orders/OrderLineMappingHelper";

export default {
	name: "VoucherInput",
	props: [
		"cartProducts",
		"orderLines",
		"vouchersRedeemed",
		"featureToggles",
		"productCatalog",
		"voucherRef",
		"applyVoucherFromModal"
	],
	data() {
		return {
			voucher: "",
			msgToAppear: "",
			isActive: false
		};
	},
	computed: {
		...mapGetters([
			"tenantSettings",
			"friendInviteCode",
			"customerDetails",
			"isSunsetPointsMember",
			"envPrefix",
			"priceRules",
			"tenantSpecificData"
		]),
		showVoucherName(){
			return this.orderLines && this.orderLines
				.map((line) => line.subLines
					.filter((line) => line.name === "VOUCHER")
					.map((line) => line.values[0].voucherName)
					.find(Boolean))
				.find(Boolean);
		},
		totalDiscount() {
			return this.totalDiscountedPrice - this.totalDiscountedPriceNoVouchers;
		},
		totalDiscountedPriceNoVouchers() {
			const self = this;
			return OrderHelperObject.getTotalPrice(
				self.cartProducts,
				self.productCatalog,
				{
					generation: DomainCoreGeneration.getGenerationByBMId(appConfig.getBusinessModelId()),
					options: {
						channel: appConfig.channelAlias(),
						deliveryMethod: this.tenantSpecificData && this.tenantSpecificData.deliveryChoice,
						customerType: evaluateCustomerType(),
						cartProductsMap: convertProductSelectionsToMap(this.cartProducts),
						exceededPriceRules: getExceededPriceRules()
					}
				}
			);
		},
		totalDiscountedPrice() {
			const self = this;
			return OrderHelperObject.getTotalPrice(
				self.cartProducts, self.productCatalog,
				{
					options: {
						vouchers: self.vouchersRedeemed,
						channel: appConfig.channelAlias(),
						deliveryMethod: this.tenantSpecificData && this.tenantSpecificData.deliveryChoice,
						customerType: evaluateCustomerType(),
						cartProductsMap: convertProductSelectionsToMap(this.cartProducts),
						exceededPriceRules: getExceededPriceRules()
					},
					generation: DomainCoreGeneration.getGenerationByBMId(appConfig.getBusinessModelId())
				}
			);
		},
		isMobileChannel() {
			return appConfig.channelAlias() === channelEnum.mobileBrowser || appConfig.channelAlias() === channelEnum.mobileApp;
		},
		isBM100() {
			return appConfig.isBM100();
		},
		isKioskChannel() {
			return appConfig.channelAlias() === channelEnum.kiosk;
		}
	},
	async mounted() {
		const {consecutiveMonthChargesNumber} = (((this.customerDetails || {}).meta || {}).subscription || {});
		if (this.featureToggles.sunsetPlusFreeDipInMenu && this.tenantSettings.numberOfConsecutiveMonthlySubscriptionCharges && consecutiveMonthChargesNumber >= this.tenantSettings.numberOfConsecutiveMonthlySubscriptionCharges.toGetFreeDip) {
			const freeDipInMenuVoucherId = await this.getVoucherIdFromPriceRule();
			if (freeDipInMenuVoucherId) {
				await voucherValidationHelper(this, freeDipInMenuVoucherId);
			}
		}
		if(this.$route.params && this.$route.params.voucherCode) {
			this.voucher = this.$route.params.voucherCode;
			await this.applyVoucher();
		}
	},
	methods: {
		async getVoucherIdFromPriceRule() {
			const priceRule = this.priceRules.find(x => x.options.label === "SunsetPlusFreeDips");
			if (!priceRule) return undefined;
			return priceRule.voucherPattern[0];
		},
		formatPriceZero,
		msgNew(){
			this.msgToAppear = "";
		},
		async applyVoucher(){
			const input = await voucherValidationHelper(this, this.voucher);
			this.handleVoucher(input);
			this.voucher = "";
			this.$emit("hide-keyboard");
		},
		handleVoucher(voucherData) {
			this.msgToAppear = voucherData.message;
			this.isActive = voucherData.success;
			this.$emit("voucherAction", {
				msgToAppear: this.msgToAppear,
				isActive: this.isActive
			});
		}
	},
	watch: {
		voucherRef(val) {
			this.voucher = val;
		},
		applyVoucherFromModal(val) {
			if(val) {
				this.applyVoucher();
			}
		}
	}
};
</script>

<style scoped lang="scss">
.voucher-body{
	display: flex;
	justify-content: space-between;
	font-family: Dispatch;
	/deep/.v-text-field__details {
		display: none;
	}
}
.apply-btn {
	margin-left: 20px;
	border-radius: 3px;
	border: 2px solid #202020;
	color: #202020 !important;
	display: inline-block;
	font-weight: bold;
	font-size: 16px;
	min-width: 72px !important;
	/deep/.v-btn__content{
		height: auto !important;
	}
}
.voucher-header{
	font-size: 12px;
	margin-bottom: 4px;
	margin-top: 8px;
	font-family: Dispatch-Bold;
}
.v-divider.divider-class {
	border-style: solid;
	border-color: gray;
	border-width: 0.3px;
}
.subtotal-part {
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-weight: bold;

	.subtotal-header {
		font-size: 18px;
	}

	.subtotal-value {
		color:#006850;
		font-size: 16px;
	}
}

.voucher-name-value {
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: 16px;
	color:#218C74;
	font-weight: bold;
}
.voucher-style {
	width: auto;
	height: 35px;
	display: inline-block;
	border: 1px solid rgb(219, 213, 213);
	border-radius: 3px;
	padding: 2px !important;
	margin: 0;
	font-family: Dispatch;
	/deep/ input {
		padding-left: 16px;
		padding-top: 3px;
	}
	::before, ::after {
		display: none;
	}
}
.apply-btn{
	display: inline-block;
	color: #63666a;
	margin: 0 0 0 16px;
	box-shadow: none !important;
}

.voucher-modal {
	visibility: hidden;
}
div.voucher-part.voucher-from-modal {
	height: 1px;
}
</style>
